import React, { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";
import SidebarItem from "./SidebarItem";

/**
 * Collapsible sidebar item that contains child navigation items
 * Used for grouping related menu items under a parent category
 */
const SidebarCollapse = ({ item, isClickable, isExpanded = true }) => {
  const { t } = useTranslation();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);

  const visibleChildren = useMemo(() => {
    return item.children?.filter((child) => !child.hide) || [];
  }, [item.children]);

  const isChildActive = useMemo(() => {
    return visibleChildren.some((child) => {
      if (child.href && !child.outsideLink) {
        return location.pathname.includes(`/${child.href}`);
      }
      return false;
    });
  }, [visibleChildren, location.pathname]);

  useEffect(() => {
    if (isChildActive) {
      setIsOpen(true);
    }
  }, [isChildActive]);

  if (visibleChildren.length === 0) {
    return null;
  }

  const effectiveOpen = isExpanded && isOpen;

  return (
    <div className={`sidebar-collapse ${isChildActive ? "active" : ""}`}>
      <div
        className={`sidebar-collapse-header p2 ${isChildActive ? "active" : ""}`}
        onClick={() => isExpanded && setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if ((e.key === "Enter" || e.key === " ") && isExpanded) {
            e.preventDefault();
            setIsOpen(!isOpen);
          }
        }}
      >
        {item.icon && (
          <FontAwesomeIcon
            icon={
              item.iconActive && isChildActive ? item.iconActive : item.icon
            }
            className="sidebar-item-icon"
          />
        )}
        <span className="sidebar-collapse-label">
          {t(`${item.tKey}sidebar`)}
        </span>
        <FontAwesomeIcon
          icon={effectiveOpen ? faChevronUp : faChevronDown}
          className="sidebar-collapse-chevron"
        />
      </div>
      <div
        className={`sidebar-collapse-children ${effectiveOpen ? "open" : "closed"}`}
      >
        {visibleChildren.map((child) => (
          <SidebarItem
            key={child.key}
            item={child}
            isClickable={isClickable}
            className="sidebar-child-item"
          />
        ))}
      </div>
    </div>
  );
};

export default SidebarCollapse;
